// src/encounter/lib/pick-encounter-questionnaire.ts
// Per PROD-1155 / Samer: the encounter endpoint returns Type-A templates only; HPI/ROS/PE
// are nested sub-groups WITHIN a questionnaire (rendered by QuestionnaireRenderer walking
// the item tree), NOT separate questionnaires. This selector picks THE encounter template
// from the matched set.
//
// PROD-1384 (cps-rule evaluation): the BFF returns EVERY Default template whose age/gender/org
// axes match — it does NOT evaluate the per-template `cps-rule`. So >1 template can come back: a
// conditional (e.g. "BMI >= 30") alongside the unconditional `{"op":"always"}` default. Selection:
//   1. a CONDITIONAL template whose rule evaluates TRUE against the encounter facts wins (the most
//      specific match — first in BFF order when several match);
//   2. else the unconditional `{"op":"always"}` default (applies to everyone);
//   3. else a template whose rule states NO condition — none carried (legacy v1, authored before
//      rules existed) or an empty `{}` (what the Studio publishes when no rule was set) — so it
//      applies to everyone;
//   4. else NOTHING — the page's "no template for this visit" path.
// A FALSE RULE NEVER SELECTS (contract G6). A conditional template whose rule evaluates false — or
// cannot be evaluated: malformed, or a fact it needs is absent — is never returned: not as the only
// candidate, and not as a first-in-list fallback. Both used to happen, so a template authored for
// another patient group could open, and be pinned to the note on its first save (PROD-2608).
//
// RANK FIRST — BUT ONLY WHEN A CANDIDATE IS SCOPED BY VISIT TYPE (TS-4, PROD-2820). QuestionBank now
// orders survivors practitioner ▸ specialty ▸ org-owned ▸ visit-type fit and stamps each with a
// response-only `cps-selection-rank`. Without rank-awareness any TRUE conditional on a general template
// (`{age>=0}`, `BMI>=30`) would silently beat a visit-type template and TS-4 would not work. But walking
// ranks is also BEHAVIOUR-CHANGING for templates that predate visit types (today any true conditional
// beats every default, whatever its scope), so — cross-model review amendment P1 — the rank walk applies
// ONLY when at least one active candidate carries visit-type scope in its `cps-scope`. Then: walk ranks
// high → low; the first rank with an applicable template wins; inside a rank today's rule applies
// unchanged (true conditional by leaf count ▸ always ▸ no-condition ▸ none; a false rule never selects).
// A rank whose templates are all false conditionals is skipped. A missing or non-integer rank is
// "unranked" and walks last. No visit-type scope anywhere, or no rank anywhere (an older server) →
// today's behaviour exactly.
import type { FhirQuestionnaire } from '@/types/template-studio-api';
import {
  CPS_RULE_EXTENSION_URL,
  CPS_SCOPE_EXTENSION_URL,
  CPS_SELECTION_RANK_EXTENSION_URL,
} from '@/types/template-studio-api';
import {
  parseCpsRule,
  isAlwaysRule,
  evaluateCpsRule,
  countRuleLeaves,
  type CpsFacts,
} from './cps-rule';

/** Match an extension URL — the short constant exactly, or the full StructureDefinition URL on a
 *  `/`-boundary (so `…/cps-rule` matches but an unrelated `…not-cps-rule` does not). */
function isExtensionUrl(url: string | undefined | null, short: string): boolean {
  return !!url && (url === short || url.endsWith(`/${short}`));
}

/** The template's cps-rule extension value, or undefined when it carries none (or a blank one). */
function cpsRuleTextOf(q: FhirQuestionnaire): string | undefined {
  const text = q.extension?.find((e) => isExtensionUrl(e.url, CPS_RULE_EXTENSION_URL))?.valueString;
  return text?.trim() ? text : undefined;
}

/** The response-only selection rank, or undefined ("unranked") when absent or not an integer. */
export function selectionRankOf(q: FhirQuestionnaire): number | undefined {
  const value = q.extension?.find((e) => isExtensionUrl(e.url, CPS_SELECTION_RANK_EXTENSION_URL))?.valueInteger;
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined;
}

/** Whether the template's authored scope names at least one visit type. Unreadable scope → false. */
export function hasVisitTypeScope(q: FhirQuestionnaire): boolean {
  const text = q.extension?.find((e) => isExtensionUrl(e.url, CPS_SCOPE_EXTENSION_URL))?.valueString;
  if (!text?.trim()) return false;
  try {
    const scope: unknown = JSON.parse(text);
    const visitTypes = (scope as { visitTypes?: unknown } | null)?.visitTypes;
    return Array.isArray(visitTypes) && visitTypes.some((v) => typeof v === 'string' && v.trim() !== '');
  } catch {
    return false;
  }
}

export function pickEncounterQuestionnaire(
  candidates: FhirQuestionnaire[],
  facts: CpsFacts = {},
  /** `rankAware` (frame v2 only, PROD-3031): walk `cps-selection-rank` groups. Off: main's single pass. */
  options: { rankAware?: boolean } = {},
): FhirQuestionnaire | null {
  // The BFF does not filter by status server-side yet, so the client does: a retired/draft
  // template is never selectable, even if it is the only match the axes returned. Dev has
  // already served a draft by mistake without this.
  const list = candidates.filter((q) => q.status === 'active');
  if (list.length === 0) return null;

  // TS-4: rank first only when a candidate is scoped by visit type AND the server ranked the list.
  const rankFirst =
    options.rankAware === true && list.some(hasVisitTypeScope) && list.some((q) => selectionRankOf(q) !== undefined);
  if (!rankFirst) return pickWithinRank(list, facts);

  // Group by rank, high → low; unranked (undefined) walks last. Each group keeps BFF order.
  const ranks = [...new Set(list.map(selectionRankOf))].sort(
    (a, b) => (b ?? Number.NEGATIVE_INFINITY) - (a ?? Number.NEGATIVE_INFINITY),
  );
  for (const rank of ranks) {
    const picked = pickWithinRank(list.filter((q) => selectionRankOf(q) === rank), facts);
    if (picked) return picked;
  }
  return null;
}

/** Today's selection rule, applied to one candidate set (the whole list, or one rank of it). */
function pickWithinRank(list: FhirQuestionnaire[], facts: CpsFacts): FhirQuestionnaire | null {
  // Parse each template's rule once (cheap; the matched set is small). `unconditional` = the rule
  // states no condition at all (none carried, or an empty `{}`); `rule` is null when a carried rule
  // does not parse — it can then never be true, and is never selected.
  const ruled = list.map((q) => {
    const text = cpsRuleTextOf(q);
    const rule = parseCpsRule(text);
    const empty = !!rule && !Array.isArray(rule) && Object.keys(rule).length === 0;
    return { q, unconditional: text === undefined || empty, rule };
  });

  // 1. A conditional template (rule present, NOT the always-default) whose rule matches the facts.
  //    When several match, the MOST SPECIFIC rule wins — ranked by leaf-condition count (PROD-1496:
  //    a specialty template's `{specialtyId==X, age>=0}` beats the general default's `{age>=0}`);
  //    ties keep BFF order (the authoring/precedence order). Inside one rank only when the TS-4 rank
  //    walk applies (see the header) — that walk is the server-side scope-specificity ordering this
  //    note used to ask for.
  const matching = ruled.filter(
    ({ rule, unconditional }) => rule && !unconditional && !isAlwaysRule(rule) && evaluateCpsRule(rule, facts),
  );
  if (matching.length > 0) {
    // Stable max-by: strictly-greater keeps the earliest of equal-specificity candidates.
    const winner = matching.reduce((best, cur) =>
      countRuleLeaves(cur.rule) > countRuleLeaves(best.rule) ? cur : best,
    );
    return winner.q;
  }

  // 2. The always-default (PROD-1384); 3. else a template whose rule states no condition;
  // 4. else nothing applies — never a conditional whose rule is false (G6).
  return (
    ruled.find(({ rule }) => isAlwaysRule(rule))?.q ??
    ruled.find(({ unconditional }) => unconditional)?.q ??
    null
  );
}
